/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import { Logger } from '@saviynt/common';
import { Box, Button, Card, ModalIsland } from '@saviynt/design-system';
import cx from 'classnames';

import type { RoleRecommendation } from '../../../services';

import { RolesService } from '../../../services/RolesService';
import { normalizeImpactLevel } from '../../../shared/utils';
import { getUserKey } from '../../../utils/cookies';
import { useInvertedLocalize } from '../../../utils/localization';

import RoleCardCondition from './RoleCardCondition/RoleCardCondition';
import RoleCardDescription from './RoleCardDescription/RoleCardDescription';
import RoleCardFooter from './RoleCardFooter/RoleCardFooter';
import RoleCardFooterCompact from './RoleCardFooterCompact/RoleCardFooterCompact';
import RoleCardHeader from './RoleCardHeader/RoleCardHeader';
import RoleCardHeaderCompact from './RoleCardHeaderCompact/RoleCardHeaderCompact';
import RoleCardMetricsCompact from './RoleCardMetricsCompact/RoleCardMetricsCompact';
import roleCardMessages from './RoleCardMessages';

import './RoleCard.css';

interface RoleCardProps {
  role: RoleRecommendation; // role data from API
  onViewDetails: (roleId: number) => void;
  className?: string;
  userAccess?: 'edit' | 'view' | 'none';
  kind?: 'fluid' | 'compact'; // layout variant (compact is default)
}

/**
 * RoleCard component displaying a single role recommendation
 * using Card structure with header, condition, and footer sections
 *
 * Supports two layout variants:
 * - compact: Vertical layout with description, metrics, and confidence pill (default)
 * - fluid: Horizontal layout with conditions and full metrics footer
 *
 * @param props - The component props
 * @returns JSX element containing the role card
 */
function RoleCard({
  role,
  onViewDetails,
  className = '',
  userAccess,
  kind = 'compact',
}: RoleCardProps) {
  const l = useInvertedLocalize(roleCardMessages);
  const [isIgnoreModalOpen, setIsIgnoreModalOpen] = useState(false);

  // Calculate if ignore button should be disabled based on user access
  const isIgnoreButtonDisabled = userAccess !== 'edit';
  const [isRejecting, setIsRejecting] = useState(false);

  const handleViewDetails = () => {
    onViewDetails(role.recommendedObjectKey);
  };

  const handleIgnoreClick = () => {
    setIsIgnoreModalOpen(true);
  };

  const handleIgnoreCancel = () => {
    setIsIgnoreModalOpen(false);
  };

  const handleIgnoreConfirm = async () => {
    const userKey = getUserKey();

    if (!userKey) {
      Logger.error('User key not found, cannot reject role');

      return;
    }

    setIsRejecting(true);

    try {
      await RolesService.rejectRole(role.recommendedObjectKey, {
        rejectedBy: parseInt(userKey, 10),
        reason: 'rejected by user',
      });

      // Store success data for banner display on dashboard
      try {
        sessionStorage.setItem(
          'roleIgnoreSuccess',
          JSON.stringify({
            timestamp: Date.now(),
          })
        );
      } catch (storageError) {
        Logger.warn(
          'SessionStorage failed, reloading without banner',
          storageError
        );
      }

      setIsIgnoreModalOpen(false);
      window.location.reload();
    } catch (error) {
      Logger.error('Failed to reject role', error);

      // Store error data for banner display on dashboard
      try {
        sessionStorage.setItem(
          'roleIgnoreError',
          JSON.stringify({
            timestamp: Date.now(),
          })
        );
      } catch (storageError) {
        Logger.warn(
          'SessionStorage failed, reloading without banner',
          storageError
        );
      }

      window.location.reload();
    } finally {
      setIsRejecting(false);
    }
  };

  return (
    <>
      <Card
        className={cx(
          'Roles-RoleCard',
          kind === 'compact' && 'Roles-RoleCard--compact',
          className
        )}>
        {role.isNew && (
          <Box
            className='Roles-RoleCard--isNew'
            role='status'
            ariaLabel='New role recommendation'>
            {l('NEW')}
          </Box>
        )}

        {kind === 'compact' ? (
          <>
            {/* compact layout: vertical stack with description and metrics */}
            <RoleCardHeaderCompact
              userCount={role.includedUsers}
              impact={normalizeImpactLevel(role.impact)}
              onIgnoreClick={handleIgnoreClick}
              isIgnoreButtonDisabled={isIgnoreButtonDisabled}
            />

            <RoleCardDescription conditions={role.conditions} />

            <RoleCardMetricsCompact
              actions={role.actions}
              entitlementsCount={role.entitlementsCount}
              yearlyRequests={role.yearlyRequestVolume}
            />

            <RoleCardFooterCompact onViewDetails={handleViewDetails} />
          </>
        ) : (
          <>
            {/* fluid layout: horizontal layout with conditions */}
            <RoleCardHeader
              userCount={role.includedUsers}
              impact={normalizeImpactLevel(role.impact)}
              onIgnoreButtonClick={handleIgnoreClick}
              isIgnoreButtonDisabled={isIgnoreButtonDisabled}
            />

            <RoleCardCondition
              conditions={role.conditions}
              actions={role.actions}
              roleId={role.recommendedObjectKey}
            />

            <RoleCardFooter
              entitlementsCount={role.entitlementsCount}
              yearlyRequests={role.yearlyRequestVolume}
              actions={role.actions}
              onViewDetails={handleViewDetails}
            />
          </>
        )}
      </Card>

      <ModalIsland
        kind='warning'
        alertTitle={l('Ignore Role?')}
        alertSubtitle={l(
          "Are you sure you want to ignore this role recommendation? You can't get it back once it's gone."
        )}
        onClose={handleIgnoreCancel}
        isOpen={isIgnoreModalOpen}
        className='Roles-RoleCard-ignoreModal'
        primaryButton={
          <Button
            type='button'
            kind='filled'
            size='medium'
            isDisabled={isRejecting}
            onClick={handleIgnoreConfirm}>
            {isRejecting ? l('Ignoring...') : l('Ignore Role')}
          </Button>
        }
        secondaryButton={
          <Button
            type='button'
            kind='ghost'
            size='medium'
            onClick={handleIgnoreCancel}>
            {l('Cancel')}
          </Button>
        }
      />
    </>
  );
}

export default RoleCard;
